<script setup lang="ts">
    import { Plus } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, ref } from 'vue';
    import { toast } from 'vue-sonner';
    import { useContraAccountData } from '@/composables/useContraAccountData';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import type { SharedData } from '@/types';
    import VoucherStatementUploadForm from '@/components/vouchers/VoucherStatementUploadForm.vue';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import VoucherStatementUploadAlertDialog from '@/components/vouchers/VoucherStatementUploadAlertDialog.vue';
    import { useVoucherStore } from '@/stores/voucher';

    const voucherStore = useVoucherStore();

    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const uploadFormRef = ref<InstanceType<typeof VoucherStatementUploadForm>>();
    const isOpen = ref(false);

    const { contraAccounts, loadingContraAccounts, fetchContraAccounts } =
        useContraAccountData(companyId);

    const { t } = useI18n();
    const queryClient = useQueryClient();
    const filesProcessing = ref(false);

    const form = useForm({
        company_id: companyId.value,
        contra_account_id: null,
        voucher_statement_id: null,
    });

    const registerUploads = () => {
        form.post(route('v2.voucher.admin.statement.register'), {
            preserveScroll: true,
            onSuccess: () => {
                uploadFormRef.value?.markFilesAsRegistered();
                toast.success(t('vouchers.multi_registration.register_success'));
                queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId] });
            },
            onError: (error: any) => {
                toast.error(t('forms.system_error'));
            },
        });
    };

    const resetModal = () => {
        form.resetAndClearErrors('voucher_statement_id', 'contra_account_id');
        form.voucher_statement_id = null;
        form.contra_account_id = null;
    };

    const openModal = async () => {
        await fetchContraAccounts();
        if (contraAccounts.value.length === 0) {
            toast.success(t('vouchers.statement_upload'), {
                description: t('vouchers.js.input_valid'),
            });
        } else {
            isOpen.value = true;
        }
    };

    const handleCloseAttempt = (newOpenState: boolean) => {
        if (!newOpenState && uploadFormRef.value?.hasUnregisteredFiles) {
            isOpen.value = true;
            voucherStore.toggleStatementUploadAlertModal(true);

            return;
        }
        resetModal();
        isOpen.value = newOpenState;
    };

    const handleFilesProcessing = (processing: boolean) => {
        filesProcessing.value = processing;
    };

    const handleDeleteStatements = () => {
        resetModal();
        isOpen.value = false;
    };
</script>

<template>
    <Dialog
        v-model:open="isOpen"
        @update:open="handleCloseAttempt"
    >
        <Button
            variant="ghost"
            width="narrow"
            class="self-start"
            size="xs"
            @click="openModal"
            :loading="loadingContraAccounts"
        >
            {{ t('vouchers.statement_upload') }}
            <Plus class="text-accent" />
        </Button>

        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>{{ t('vouchers.statement_upload') }}</DialogTitle>
            </DialogHeader>

            <DialogBody>
                <VoucherStatementUploadForm
                    ref="uploadFormRef"
                    :form="form"
                    :contra-accounts="contraAccounts"
                    @files-processing="handleFilesProcessing"
                />
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="resetModal"
                    >
                        {{ t('forms.cancel') }}
                    </Button>
                </DialogClose>

                <Button
                    :disabled="
                        form.processing || !form.data().voucher_statement_id || filesProcessing
                    "
                    :loading="form.processing"
                    size="sm"
                    @click="registerUploads"
                >
                    {{ t('button-text.register-economic') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
    <VoucherStatementUploadAlertDialog @delete-statements="handleDeleteStatements" />
</template>
